/**
 * HrisGuard — route-level access gate for HRIS screens. Blocks the screen
 * before it mounts (so no restricted data is even fetched):
 *
 *   · allowed        → render the screen
 *   · limited user
 *     hitting a screen they lack (e.g. Operations opening /hris/payroll)
 *                     → redirect to their first allowed screen
 *   · no HRIS access → a Restricted notice (not a redirect, so the URL
 *                       doesn't reveal whether the screen exists)
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type { HrisScreenKey } from "./hrisSettings";
import { useHrisAccess, firstAllowedRoute } from "./hrisAccess";

export function HrisGuard({ screen, requireFull, children }: {
  /** Screen this route shows; omit + requireFull for Settings/Discovery. */
  screen?: HrisScreenKey;
  /** Route needs FULL access (Settings, Discovery). */
  requireFull?: boolean;
  children: ReactNode;
}) {
  const access = useHrisAccess();
  const ok = requireFull ? access.full : screen ? access.screens.has(screen) : access.hasAny;
  if (ok) return <>{children}</>;

  // Not allowed here, but has some other HRIS screen → send them to it.
  if (access.hasAny) {
    const fallback = firstAllowedRoute(access);
    if (fallback) return <Navigate to={fallback} replace />;
  }

  // No HRIS access at all.
  return (
    <div className="restricted-screen">
      <div className="restricted-icon" aria-hidden="true">🔒</div>
      <div className="restricted-title">Restricted</div>
      <div className="restricted-message">
        HRIS · Payroll holds confidential employee pay data and is limited to authorized HR and Finance
        accounts. Contact the owner if you believe you should have access.
      </div>
    </div>
  );
}
